/**
 * CorrespondenceDetailModal - Sprint Y Block E
 * 
 * Modal for viewing full email body and parsed data.
 * Display-only in Block E. Edit/confirm flows are Block G (review queue).
 * 
 * HITL CRITICAL: 
 * - Proposed data is clearly badged as "navrženo / proposed — k ověření"
 * - NO commit actions in this modal (Block E is display-only)
 * - Edit affordance routes to review queue (Block G), not direct commit
 */

import { useLanguage } from "../../contexts/LanguageContext";
import { 
  Send, Inbox, Clock, CheckCircle, XCircle, AlertTriangle,
  Mail, Paperclip, X, AlertCircle, Calendar, FileText,
  User, Plane, Shield, ExternalLink, Copy
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../ui/tabs";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import { toast } from "sonner";

// Source category colors
const SOURCE_COLORS = {
  regulator: "text-blue-500",
  atc: "text-purple-500",
  operator_authority: "text-teal-500",
  police: "text-red-500",
  fire: "text-orange-500",
  energy: "text-yellow-500",
  insurance: "text-green-500",
  other: "text-slate-500",
};

export const CorrespondenceDetailModal = ({ 
  correspondence, 
  open, 
  onClose,
  onGoToReviewQueue, // Optional: navigate to Block G review queue
}) => {
  const { translate: t, language } = useLanguage();
  
  if (!correspondence) return null;
  
  const {
    id,
    subject,
    sender_email,
    sender_name,
    recipient_email,
    received_at,
    direction = "inbound",
    status = "pending_review",
    source_category,
    source_name_cs,
    source_name_en,
    attachments = [],
    body_text = "",
    body_html,
    message_id,
    in_reply_to,
    extracted = {},
    needs_review,
    proposed_authorization_id,
    proposed_mission_id,
    proposed_pilot_id,
    confirmed_authorization_id,
    confirmed_mission_id,
    confirmed_pilot_id,
    parsed_at,
    parsing_backend,
  } = correspondence;
  
  const sourceName = language === "cs" ? source_name_cs : source_name_en;
  const sourceColor = SOURCE_COLORS[source_category] || "text-slate-500";
  
  const hasProposedLinks = proposed_authorization_id || proposed_mission_id || proposed_pilot_id;
  const hasConfirmedLinks = confirmed_authorization_id || confirmed_mission_id || confirmed_pilot_id;
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  
  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(language === "cs" ? "Zkopírováno" : "Copied");
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="correspondence-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <Mail className={`w-5 h-5 ${sourceColor}`} />
            <span className="truncate">{subject}</span>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 text-sm">
            {direction === "inbound" ? (
              <>
                <Inbox className="w-4 h-4" />
                {t("correspondence.from")}: {sender_name || sender_email}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {t("correspondence.to")}: {recipient_email}
              </>
            )}
            {sourceName && (
              <>
                <span className="text-muted-foreground">•</span>
                <span>{sourceName}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="body" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="body" data-testid="tab-body">{t("correspondence.body")}</TabsTrigger>
            <TabsTrigger value="extracted" data-testid="tab-extracted">{t("correspondence.extractedData")}</TabsTrigger>
            <TabsTrigger value="metadata" data-testid="tab-metadata">{t("correspondence.metadata")}</TabsTrigger>
          </TabsList>
          
          {/* Body Tab */}
          <TabsContent value="body" className="flex-1 min-h-0">
            <ScrollArea className="h-[400px] rounded-md border p-4">
              <div className="whitespace-pre-wrap text-sm font-mono">
                {body_text || (language === "cs" ? "Žádný obsah" : "No content")}
              </div>
            </ScrollArea>
          </TabsContent>
          
          {/* Extracted Data Tab */}
          <TabsContent value="extracted" className="flex-1 min-h-0">
            <ScrollArea className="h-[400px] rounded-md border p-4">
              <div className="space-y-4">
                {/* Summary */}
                {(extracted.summary_cs || extracted.summary_en) && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">{t("correspondence.summary")}</h4>
                    <p className="text-sm text-muted-foreground">
                      {language === "cs" ? extracted.summary_cs : extracted.summary_en}
                    </p>
                  </div>
                )}
                
                {/* Reference numbers */}
                {extracted.reference_number && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">{t("correspondence.referenceNumber")}</h4>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono">{extracted.reference_number}</Badge>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2"
                        onClick={() => copyToClipboard(extracted.reference_number)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Deadline */}
                {extracted.deadline && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">{t("correspondence.deadline")}</h4>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span>{formatDate(extracted.deadline)}</span>
                      {new Date(extracted.deadline) < new Date() && (
                        <Badge variant="destructive" className="text-xs">
                          {t("correspondence.overdue")}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Action required */}
                {extracted.action_required && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">{t("correspondence.actionRequired")}</h4>
                    <p className="text-sm text-muted-foreground">{extracted.action_required}</p>
                  </div>
                )}
                
                {/* Priority */}
                {extracted.priority && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">{t("correspondence.priority")}</h4>
                    <Badge 
                      variant={extracted.priority === "critical" ? "destructive" : 
                               extracted.priority === "high" ? "default" : "outline"}
                    >
                      {extracted.priority}
                    </Badge>
                  </div>
                )}
                
                {/* Keywords */}
                {extracted.keywords && extracted.keywords.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">Keywords</h4>
                    <div className="flex flex-wrap gap-1">
                      {extracted.keywords.map((kw, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">{kw}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <Separator />
                
                {/* HITL: Links section - CRITICAL DISTINCTION */}
                <div>
                  <h4 className="text-sm font-medium mb-2">{t("correspondence.linkedTo")}</h4>
                  
                  {!hasConfirmedLinks && !hasProposedLinks && (
                    <p className="text-sm text-muted-foreground">{t("correspondence.noLinkedEntities")}</p>
                  )}
                  
                  {/* Confirmed links - GREEN */}
                  {hasConfirmedLinks && (
                    <div className="space-y-2 mb-3">
                      <p className="text-xs font-medium text-green-600">{t("correspondence.confirmedLink")}:</p>
                      {confirmed_authorization_id && (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                            <Shield className="w-3 h-3 mr-1" />
                            {t("correspondence.authorization")}
                          </Badge>
                          <span className="text-xs text-green-600">{t("correspondence.confirmedBadge")}</span>
                          <code className="text-xs text-muted-foreground">{confirmed_authorization_id}</code>
                        </div>
                      )}
                      {confirmed_mission_id && (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                            <Plane className="w-3 h-3 mr-1" />
                            {t("correspondence.mission")}
                          </Badge>
                          <span className="text-xs text-green-600">{t("correspondence.confirmedBadge")}</span>
                          <code className="text-xs text-muted-foreground">{confirmed_mission_id}</code>
                        </div>
                      )}
                      {confirmed_pilot_id && (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                            <User className="w-3 h-3 mr-1" />
                            {t("correspondence.pilot")}
                          </Badge>
                          <span className="text-xs text-green-600">{t("correspondence.confirmedBadge")}</span>
                          <code className="text-xs text-muted-foreground">{confirmed_pilot_id}</code>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Proposed links - AMBER, clearly marked as "navrženo — k ověření" */}
                  {hasProposedLinks && (
                    <div className="space-y-2 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                        <p className="text-xs font-medium text-amber-600">{t("correspondence.proposedLink")}:</p>
                      </div>
                      <p className="text-xs text-amber-600/80 italic">{t("correspondence.awaitingReview")}</p>
                      
                      {proposed_authorization_id && !confirmed_authorization_id && (
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                            <Shield className="w-3 h-3 mr-1" />
                            {t("correspondence.authorization")}
                          </Badge>
                          <span className="text-xs text-amber-600">{t("correspondence.proposedBadge")}</span>
                          <code className="text-xs text-muted-foreground">{proposed_authorization_id}</code>
                        </div>
                      )}
                      {proposed_mission_id && !confirmed_mission_id && (
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                            <Plane className="w-3 h-3 mr-1" />
                            {t("correspondence.mission")}
                          </Badge>
                          <span className="text-xs text-amber-600">{t("correspondence.proposedBadge")}</span>
                          <code className="text-xs text-muted-foreground">{proposed_mission_id}</code>
                        </div>
                      )}
                      {proposed_pilot_id && !confirmed_pilot_id && (
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                            <User className="w-3 h-3 mr-1" />
                            {t("correspondence.pilot")}
                          </Badge>
                          <span className="text-xs text-amber-600">{t("correspondence.proposedBadge")}</span>
                          <code className="text-xs text-muted-foreground">{proposed_pilot_id}</code>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          </TabsContent>
          
          {/* Metadata Tab */}
          <TabsContent value="metadata" className="flex-1 min-h-0">
            <ScrollArea className="h-[400px] rounded-md border p-4">
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">{t("correspondence.received")}:</span>
                  <span className="col-span-2">{formatDate(received_at)}</span>
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">{t("correspondence.from")}:</span>
                  <span className="col-span-2">{sender_name ? `${sender_name} <${sender_email}>` : sender_email}</span>
                </div>
                
                {recipient_email && (
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-muted-foreground">{t("correspondence.to")}:</span>
                    <span className="col-span-2">{recipient_email}</span>
                  </div>
                )}
                
                <Separator />
                
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">{t("correspondence.messageId")}:</span>
                  <span className="col-span-2 font-mono text-xs break-all">{message_id || "-"}</span>
                </div>
                
                {in_reply_to && (
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-muted-foreground">{t("correspondence.inReplyTo")}:</span>
                    <span className="col-span-2 font-mono text-xs break-all">{in_reply_to}</span>
                  </div>
                )}
                
                <Separator />
                
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="col-span-2">
                    <Badge variant="outline">{t(`correspondence.status.${status}`)}</Badge>
                  </span>
                </div>
                
                {parsed_at && (
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-muted-foreground">Parsed:</span>
                    <span className="col-span-2">{formatDate(parsed_at)} ({parsing_backend || "unknown"})</span>
                  </div>
                )}
                
                {/* Attachments */}
                {attachments.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <span className="text-muted-foreground">{t("correspondence.attachments")} ({attachments.length}):</span>
                      <div className="mt-2 space-y-1">
                        {attachments.map((att, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <Paperclip className="w-3 h-3 text-muted-foreground" />
                            <span>{att.filename}</span>
                            <span className="text-muted-foreground">({Math.round((att.size_bytes || 0) / 1024)} KB)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="flex-shrink-0">
          {/* HITL: Review queue button - routes to Block G, not direct commit */}
          {needs_review && onGoToReviewQueue && (
            <Button 
              variant="outline" 
              onClick={onGoToReviewQueue}
              data-testid="go-to-review-queue"
            >
              <Clock className="w-4 h-4 mr-2" />
              {t("correspondence.goToReviewQueue")}
            </Button>
          )}
          <Button variant="default" onClick={onClose}>
            {t("actions.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CorrespondenceDetailModal;
